import { CreateReferralCommand } from "./createReferralCommand";
import { ReferralDTO } from "./referralDTO";
import { UpdateReferralCommand } from "./updateReferralCommand";

import { Payee as PayeeDTO } from "./payee";
import {Note as NoteDTO} from "./note";

export class Referral {

    public static readonly TYPES = ["TRAVEL"];
    public static readonly STATUS = ["ACTIVE", "INACTIVE"];

    constructor(private _id: string, private _referralCode: string,
        private _payees: Payee[] = [],
        private _type: Referral.TypeEnum,
        private _status: Referral.StatusEnum,
        private _notes: Note[] = []) {
        if (this.notes.length < 1) {
            this.notes.push(new Note("", "", ""))
        }
        if (this.payees.length < 1) {
            this.addPayee(null);
        }

    }

    public get id() {
        return this._id;
    }
    public get referralCode() {
        return this._referralCode;
    }
    public get payees() {
        return this._payees;
    }
    public get type() {
        return this._type;
    }
    public get status() {
        return this._status;
    }
    public get notes() {
        return this._notes;
    }

    public set referralCode(referralCode: string) {
        this._referralCode = referralCode;
    }
    public set payees(payees: Payee[]) {
        this._payees = payees;
    }
    public set type(type: Referral.TypeEnum) {
        this._type = type;
    }
    public set status(status: Referral.StatusEnum) {
        this._status = status;
    }
    public set notes(notes: Note[]) {
        this._notes = notes;
    }

    public static fromDTO(dto: ReferralDTO): Referral | undefined {
        if (dto.id && dto.referralCode && dto.type && dto.status && dto.payees) {
            return new Referral(
                dto.id,
                dto.referralCode,
                this.fromPayeesDTO(dto.payees),
                dto.type,
                dto.status,
                this.fromNotesDTO(dto.notes)
            )
        } else {
            return undefined;
        }
    }

    public toUpdateReferralCommand(): UpdateReferralCommand {
        if (this) {
            return {
                id: this.id,
                referralCode: this.referralCode,
                type: this.type,
                status: this.status,
                payees: this.toPayees(),
                notes: this.toNotes()
            }
        }
        return {};
    }

    public toCreateReferralCommand(): CreateReferralCommand {
        if (this) {
            return {
                referralCode: this.referralCode,
                type: this.type,
                status: this.status,
                payees: this.toPayees(),
                notes: this.toNotes()
            }
        }
        return {};
    }

    private toNotes(): NoteDTO[] {
        let noteList: NoteDTO[] = [];
        if (this.notes) {
            this.notes.forEach(n => {
                noteList.push(n.toNote());
            });
            return noteList;
        }
        return [];
    }

    private toPayees(): PayeeDTO[] {
        let payeeList: PayeeDTO[] = [];
        if (this.payees) {
            this.payees.forEach(p => {
                payeeList.push(p.toPayee());
            });
            return payeeList;
        }
        return [];
    }

    private static fromPayeesDTO(payees: PayeeDTO[]): Payee[] {
        let payeeList: Payee[] = [];
        if (payees) {
            payees.forEach(p => {
                payeeList.push(Payee.fromDTO(p));
            });
            return payeeList;
        }
        return [];
    }

    private static fromNotesDTO(notes: NoteDTO[] | undefined): Note[] {
        let noteList: Note[] = [];
        if (notes) {
            notes.forEach(n => {
                noteList.push(Note.fromDTO(n));
            });
            return noteList;
        }
        return [];
    }

    public getTypes() {
        return Referral.TYPES;
    }

    public getStatusTypes() {
        return Referral.STATUS;
    }

    public addPayee(payee: Payee | null) {
        if (payee) {
            this.payees.push(payee);
        } else {
            this.payees.push(new Payee("", "", new Commission(0, true)));
        }
    }

    public removeLastPayee() {
        this.payees.pop();
    }

    public addNote(note: Note | null) {
        if (note) {
            this.notes.push(note);
        } else {
            this.notes.push(new Note(
                "", "", "", undefined, undefined
            ));
        }
    }

    private areNotesValid(): boolean {
        if (this.notes) {
            return this.notes.filter(note => !note.isValid()).length == 0;
        }
        return true; // notes are optional
    }

    private arePayeesValid() {
        if (this.payees && this.payees.length > 0) {
            return this.payees.filter(payee => !payee.isValid()).length == 0;
        }
        return false;
    }

    public isValid(): boolean {
        return this.areNotesValid() && this.arePayeesValid()
            && this.referralCode != "" && this.referralCode != null && this.referralCode != undefined
            && this.status != null && this.status != undefined
            && this.type != null && this.type != undefined;
    }

    public cleanUpBeforeSaving() {
        // delete any notes with blank comments
        if (this.notes) {
            this.notes = this.notes.filter(note => note.comment !== null && note.comment !== "" && note.comment !== undefined);
            if (this.notes.length == 1 && this.notes[0].createdDate === undefined) {
                this.notes[0].createdDate = new Date();
                this.notes[0].lastModifiedDate = new Date();
            }
        }
    }
}

export class Payee {

    constructor(private _legacyOrganizationId: string, private _organizationId: string, private _commission: Commission) { }

    public get legacyOrganizationId() {
        return this._legacyOrganizationId;
    }
    public get organizationId() {
        return this._organizationId;
    }
    public get commission() {
        return this._commission;
    }

    public set legacyOrganizationId(legacyOrganizationId: string) {
        this._legacyOrganizationId = legacyOrganizationId;
    }
    public set organizationId(organizationId: string) {
        this._organizationId = organizationId;
    }
    public set commission(commission: Commission) {
        this._commission = commission;
    }

    public static fromDTO(payee: PayeeDTO): Payee {
        return new Payee(payee.legacyOrganizationId ? payee.legacyOrganizationId : "",
            payee.organizationId ? payee.organizationId : "",
            new Commission(payee.commission?.amount ? payee.commission.amount : 0,
                payee.commission?.percentage !== undefined ? payee.commission.percentage : true));

    }

    public toPayee(): PayeeDTO {
        return {
            legacyOrganizationId: this.legacyOrganizationId,
            organizationId: this.organizationId,
            commission: { amount: this.commission.amount, percentage: this.commission.percentage }
        }
    }

    public isValid(): boolean {
        return this.legacyOrganizationId != ""
            && this.legacyOrganizationId != null && this.legacyOrganizationId != undefined
            && this.commission.isValid();
    }
}

export class Note {
    constructor(private _author?: string, private _subject?: string,
        private _comment?: string, private _createdDate?: Date, private _lastModifiedDate?: Date) { }

    public get comment(): string | undefined {
        return this._comment;
    }
    public get subject() {
        return this._subject;
    }
    public get author() {
        return this._author;
    }
    public get createdDate() {
        return this._createdDate;
    }
    public get lastModifiedDate() {
        return this._lastModifiedDate;
    }

    public set comment(comment: string | undefined) {
        this._comment = comment;
    }
    public set subject(subject: string | undefined) {
        this._subject = subject;
    }
    public set author(author: string | undefined) {
        this._author = author;
    }
    public set createdDate(createdDate: Date | undefined) {
        this._createdDate = createdDate;
    }
    public set lastModifiedDate(lastModifiedDate: Date | undefined) {
        this._lastModifiedDate = lastModifiedDate;
    }

    public static fromDTO(note: NoteDTO): Note {
        return new Note(note.author, note.subject, note.comment,
            note.createdDate?.date ?
                new Date(note.createdDate?.date) : undefined,
            note.lastModifiedDate?.date ?
                new Date(note.lastModifiedDate?.date) : undefined);
    }

    public toNote(): NoteDTO {
        return {
            author: this.author, subject: this.subject,
            comment: this.comment, createdDate: { date: this.createdDate?.toISOString() },
            lastModifiedDate: { date: this.lastModifiedDate?.toISOString() }
        };
    }

    public isValid(): boolean {
        return true;
    }

}

export class Commission {

    constructor(private _amount: number, private _percentage: boolean) {
        if(_percentage == null || _percentage == undefined){
            this.percentage = true;
        }
     }

    public get amount() {
        return this._amount;
    }

    public get percentage() {
        return this._percentage;
    }

    public set amount(amount: number) {
        this._amount = amount;
    }

    public set percentage(percentage: boolean) {
        this._percentage = true;
    }

    public isValid(): boolean {
        return this.amount != null && this.amount != undefined
            && this.percentage != null && this.percentage != undefined;
    }
}

export declare namespace Referral {
    type TypeEnum = 'TRAVEL';
    const TypeEnum: {
        Travel: "TRAVEL";
    };
    type StatusEnum = 'ACTIVE' | 'INACTIVE';
    const StatusEnum: {
        Active: StatusEnum;
        Inactive: StatusEnum;
    };
}